import { useState, useEffect, useRef, useMemo } from 'react';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { useWallet } from '@/hooks/wallet';
import BigNumber from 'bignumber.js';
import { StakeOption, Process } from '@/types/stake';
import { useCoinInfoContext } from '@/hooks/useCoinInfo';
import { PoolButtonType, NeedSlippageType } from '@/types/pool';
import { scTrack } from '@web3/base-tracking';
import { Event, Page } from '@/config/track-config';
import { useAccountInfoContext } from '@/hooks/useAccountInfo';
import { getButtonIsHidden } from '@/containers/pool/operation/utils';
import { checkDecimal } from '@/utils/num';
import { isLBTCvPool, isLombardPool } from '@/utils/common';
import { useLombardSign } from './third/lombardHooks/useLombardSign';
/**
 * 滑点相关hook
 */
export const useSlippage = () => {
  const { poolInfo } = usePoolInfoContext();

  const [slippage, setSlippage] = useState(
    // 如果可以让用户滑点，默认值为0.5，否则为0并且不让用户设置滑点
    poolInfo.needSlippage === NeedSlippageType.Need ? '0.5' : '0.5'
  );

  const onChangeSlippage = (v: string) => {
    setSlippage(v);
  };

  return {
    slippage,
    onChangeSlippage,
  };
};

/**
 * 当前token相关数据，包含输入金额和当前选择token信息
 */
export const useCurrentToken = () => {
  const [stakeAmount, setStakeAmount] = useState('');
  const { poolInfo } = usePoolInfoContext();
  const { coinInfoStore } = useCoinInfoContext();
  const [process, setProcess] = useState(Process.None);

  const [currentStakeTokenKey, setCurrentStakeTokenKey] = useState(
    `${isLombardPool(poolInfo.id) || isLBTCvPool(poolInfo.id) ? poolInfo?.whiteList?.[0]?.coinDetailJson?.chainCode : poolInfo.chainCode}${poolInfo.whiteList[0].contractAddress}`
  );
  const currentStakeToken = useMemo(
    () => coinInfoStore[currentStakeTokenKey],
    [coinInfoStore, currentStakeTokenKey]
  );
  const stakeAmountValue =
    currentStakeToken?.price && stakeAmount
      ? BigNumber(
          BigNumber(stakeAmount).times(currentStakeToken?.price).toFixed(2)
        ).toFormat()
      : '';

  const aprPerDay = BigNumber(poolInfo.apy ?? 0)
    .dividedBy(365)
    .dividedBy(100)
    .times(stakeAmount)
    .times(currentStakeToken?.price)
    .toString();

  const onChangeCurrentToken = (key: string) => {
    setCurrentStakeTokenKey(key);
  };

  const onChangeStakeAmount = (amount: string) => {
    // 针对SOL池子判断输入数字小数点是否超过8位
    if (poolInfo.chainCode === 'SOL' && !checkDecimal(amount)) {
      return;
    }
    setStakeAmount(amount);
  };

  const onChangeProcess = (step: Process) => {
    setProcess(step);
  };

  return {
    stakeAmount,
    stakeAmountValue,
    currentStakeToken,
    aprPerDay,
    process,
    onChangeStakeAmount,
    onChangeCurrentToken,
    onChangeProcess,
  };
};

/**
 * 当前按钮相关状态
 */
export const useButtonStatus = (option: Partial<StakeOption>) => {
  const { stakeAmount, gasFeeInfo, aprPerDay, gasFeePrice, onChangeProcess } =
    option as StakeOption;
  const { isLogin, openLoginModal } = useWallet();
  const { poolInfo } = usePoolInfoContext();
  const textIsError = useRef(false);
  const { accountInfo } = useAccountInfoContext();
  const stakeIsHidden = getButtonIsHidden(
    PoolButtonType.Stake,
    poolInfo.poolExtendDTO
  );
  const gasFeeIsGreatThanProfit = BigNumber(aprPerDay)
    .times(30)
    .lt(gasFeePrice);

  const buttonDisabled = (() => {
    if (!isLogin) {
      return false;
    }

    if (BigNumber(stakeAmount || 0).lte(0)) {
      return true;
    }

    if (!gasFeeInfo?.gasMax) {
      return true;
    }

    if (textIsError.current) {
      return true;
    }
  })();

  const onChangeTextIsError = (v: boolean) => {
    textIsError.current = v;
  };

  const onClickStake = () => {
    if (!isLogin) {
      openLoginModal();
    }
    scTrack(Event.Web3UserClick, {
      page_name: Page.PoolDetail,
      button_name: 'Stake',
      spmID: poolInfo.id,
    });

    if (accountInfo?.isShowDisclaimer) {
      onChangeProcess(Process.Disclaime);
      return;
    }

    if (gasFeeIsGreatThanProfit) {
      onChangeProcess(Process.GasFeeTip);
      return;
    }

    onChangeProcess(Process.Confirm);
  };

  return {
    stakeIsHidden,
    buttonDisabled,
    gasFeeIsGreatThanProfit,
    onClickStake,
    onChangeTextIsError,
  };
};

export const usePvTrack = () => {
  const { poolInfo } = usePoolInfoContext();
  useEffect(() => {
    scTrack(Event.Web3PageView, {
      page_name: Page.StakingInitial,
      spmName: poolInfo.poolName,
    });
  }, []);
};
